/**
 * MarkdownEditor Component
 *
 * A split-pane markdown editor with live preview and autosave.
 * Left side: text editor for markdown
 * Right side: rendered preview using react-markdown
 *
 * Features:
 * - Autosave with debounce (saves 1s after last keystroke)
 * - Manual save with Cmd/Ctrl+S
 * - Save status indicator
 * - Synced scroll between editor and preview (optional)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFileStore } from '@/stores/fileStore';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import {
  Save,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  Edit3,
  Columns,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ============================================
// Types
// ============================================

interface MarkdownEditorProps {
  /** File path for saving */
  path: string;
  /** Initial content */
  content: string;
  /** File name (for display) */
  filename: string;
  /** Additional CSS classes */
  className?: string;
}

type ViewMode = 'split' | 'edit' | 'preview';
type SaveStatus = 'saved' | 'modified' | 'saving' | 'error';

// ============================================
// Constants
// ============================================

const AUTOSAVE_DELAY = 1000; // 1 second after last keystroke

// ============================================
// MarkdownEditor Component
// ============================================

export function MarkdownEditor({
  path,
  content: initialContent,
  filename,
  className,
}: MarkdownEditorProps) {
  const { setFileContent, markFileModified } = useFileStore();

  // Editor state
  const [content, setContent] = useState(initialContent);
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refs for autosave timer
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  // Track if content has been modified from initial
  const isModified = content !== initialContent;

  // Update local content when file changes externally
  useEffect(() => {
    setContent(initialContent);
    setSaveStatus('saved');
    setSaveError(null);
  }, [initialContent, path]);

  // Save function
  const saveFile = useCallback(async () => {
    if (!isModified) return;

    setSaveStatus('saving');
    setSaveError(null);

    try {
      await api.files.write(path, content);
      setSaveStatus('saved');
      markFileModified(path, false);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  }, [path, content, isModified, markFileModified]);

  // Handle content change with autosave
  const handleContentChange = useCallback(
    (newContent: string) => {
      setContent(newContent);
      setFileContent(path, newContent);
      setSaveStatus('modified');

      // Clear existing timer
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }

      // Set new autosave timer
      autosaveTimerRef.current = setTimeout(async () => {
        setSaveStatus('saving');
        try {
          await api.files.write(path, newContent);
          setSaveStatus('saved');
          markFileModified(path, false);
        } catch (err) {
          setSaveStatus('error');
          setSaveError(err instanceof Error ? err.message : 'Failed to save');
        }
      }, AUTOSAVE_DELAY);
    },
    [path, setFileContent, markFileModified]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl+S to save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveFile();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveFile]);

  // Cleanup autosave timer on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  // Save status indicator
  const SaveStatusIndicator = useMemo(() => {
    switch (saveStatus) {
      case 'saved':
        return (
          <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
            <Check className="h-3.5 w-3.5" />
            <span>Saved</span>
          </div>
        );
      case 'modified':
        return (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <Edit3 className="h-3.5 w-3.5" />
            <span>Unsaved</span>
          </div>
        );
      case 'saving':
        return (
          <div className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Saving...</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400" title={saveError || undefined}>
            <AlertCircle className="h-3.5 w-3.5" />
            <span>Error</span>
          </div>
        );
    }
  }, [saveStatus, saveError]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-900">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
            {filename}
          </span>
          {SaveStatusIndicator}
        </div>

        <div className="flex items-center gap-1">
          {/* View mode buttons */}
          <Button
            variant={viewMode === 'edit' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={() => setViewMode('edit')}
            title="Edit only"
          >
            <Edit3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'split' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={() => setViewMode('split')}
            title="Split view"
          >
            <Columns className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'preview' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={() => setViewMode('preview')}
            title="Preview only"
          >
            <Eye className="h-4 w-4" />
          </Button>

          <div className="w-px h-4 bg-surface-300 dark:bg-surface-600 mx-1" />

          {/* Save button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={saveFile}
            disabled={!isModified || saveStatus === 'saving'}
            title="Save (⌘S)"
          >
            <Save className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Editor / Preview area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor pane */}
        {(viewMode === 'edit' || viewMode === 'split') && (
          <div
            className={cn(
              'flex flex-col overflow-hidden',
              viewMode === 'split' ? 'w-1/2 border-r border-surface-200 dark:border-surface-700' : 'w-full'
            )}
          >
            <textarea
              ref={editorRef}
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              className={cn(
                'flex-1 w-full p-4 resize-none',
                'bg-white dark:bg-surface-950',
                'text-surface-900 dark:text-surface-100',
                'font-mono text-sm leading-relaxed',
                'focus:outline-none',
                'placeholder:text-surface-400'
              )}
              placeholder="Start writing markdown..."
              spellCheck={false}
            />
          </div>
        )}

        {/* Preview pane */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div
            ref={previewRef}
            className={cn(
              'overflow-auto',
              'bg-white dark:bg-surface-950',
              viewMode === 'split' ? 'w-1/2' : 'w-full'
            )}
          >
            <article
              className={cn(
                'prose prose-sm dark:prose-invert max-w-none',
                'px-8 py-6',
                // Prose customization for TelcoOS theme
                'prose-headings:text-surface-900 dark:prose-headings:text-surface-100',
                'prose-p:text-surface-700 dark:prose-p:text-surface-300',
                'prose-strong:text-surface-900 dark:prose-strong:text-surface-100',
                'prose-a:text-primary-600 dark:prose-a:text-primary-400',
                'prose-a:no-underline hover:prose-a:underline',
                'prose-code:text-primary-700 dark:prose-code:text-primary-300',
                'prose-code:bg-surface-100 dark:prose-code:bg-surface-800',
                'prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded',
                'prose-code:before:content-none prose-code:after:content-none',
                'prose-pre:bg-surface-900 dark:prose-pre:bg-surface-900',
                'prose-pre:border prose-pre:border-surface-200 dark:prose-pre:border-surface-800',
                'prose-blockquote:border-primary-500',
                'prose-blockquote:text-surface-600 dark:prose-blockquote:text-surface-400',
                'prose-hr:border-surface-200 dark:prose-hr:border-surface-800',
                'prose-th:text-surface-900 dark:prose-th:text-surface-100',
                'prose-td:text-surface-700 dark:prose-td:text-surface-300',
                'prose-table:border prose-table:border-surface-200 dark:prose-table:border-surface-700',
                'prose-th:bg-surface-100 dark:prose-th:bg-surface-800',
                'prose-th:px-3 prose-th:py-2',
                'prose-td:px-3 prose-td:py-2',
                'prose-td:border prose-td:border-surface-200 dark:prose-td:border-surface-700',
                'prose-th:border prose-th:border-surface-200 dark:prose-th:border-surface-700',
                'prose-img:rounded-lg prose-img:shadow-md',
                'prose-li:text-surface-700 dark:prose-li:text-surface-300',
                'prose-li:marker:text-surface-400 dark:prose-li:marker:text-surface-500'
              )}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children, ...props }) => (
                    <h1 {...props} className="scroll-mt-4">
                      {children}
                    </h1>
                  ),
                  h2: ({ children, ...props }) => (
                    <h2 {...props} className="scroll-mt-4">
                      {children}
                    </h2>
                  ),
                  h3: ({ children, ...props }) => (
                    <h3 {...props} className="scroll-mt-4">
                      {children}
                    </h3>
                  ),
                  a: ({ href, children, ...props }) => {
                    const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
                    return (
                      <a
                        href={href}
                        {...props}
                        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {children}
                      </a>
                    );
                  },
                  code: ({ className, children, ...props }) => {
                    const isInline = !className;
                    if (isInline) {
                      return (
                        <code {...props} className="not-prose px-1.5 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-primary-700 dark:text-primary-300 text-sm font-mono">
                          {children}
                        </code>
                      );
                    }
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';
                    return (
                      <code {...props} className={className} data-language={language}>
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children, ...props }) => (
                    <pre
                      {...props}
                      className="not-prose overflow-x-auto rounded-lg bg-surface-900 dark:bg-surface-900 border border-surface-200 dark:border-surface-800 p-4"
                    >
                      {children}
                    </pre>
                  ),
                  input: ({ type, checked, ...props }) => {
                    if (type === 'checkbox') {
                      return (
                        <input
                          type="checkbox"
                          checked={checked}
                          readOnly
                          className="mr-2 accent-primary-600"
                          {...props}
                        />
                      );
                    }
                    return <input type={type} {...props} />;
                  },
                  table: ({ children, ...props }) => (
                    <div className="overflow-x-auto my-4">
                      <table {...props} className="w-full border-collapse">
                        {children}
                      </table>
                    </div>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          </div>
        )}
      </div>
    </div>
  );
}

export default MarkdownEditor;
